// apps/pwa-pos/src/components/ProductCard.tsx
// Sprint 99 (audit-s99 MIB-25) — extraído de SalePage.tsx para mantener
// el page <500 líneas y permitir tests aislados. Comportamiento idéntico.

import type { DragEvent as ReactDragEvent, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Minus, Plus, X } from 'lucide-react';
import { ProductImage } from './ProductImage';
import { formatMoney } from '@/lib/format';
import type { CatalogItem } from '@/lib/catalog';
import {
  blockedSaleMessage,
  catalogStockKind,
  residualStock,
  type CatalogStockKind,
} from '@/lib/stock-display';

const LOW_STOCK_THRESHOLD = 5;

/**
 * Serializado en dataTransfer para drag-and-drop al carrito.
 * El id del producto + presentacion bastan para que el drop target
 * (CartPanel) pueda llamar `addToCart({ id, presentacion_id })` sin
 * necesidad de lookup adicional.
 */
export interface DraggedProduct {
  id: string;
  presentacion_id: string;
}

export const DRAG_MIME = 'application/x-mibodega-product';

export interface ProductCardProps {
  prod: CatalogItem;
  qty: number;
  selected: boolean;
  maxReached: boolean;
  monedaCodigo: string;
  tabIndex: number;
  buttonRef: (el: HTMLButtonElement | null) => void;
  onKeyDown: (e: ReactKeyboardEvent) => void;
  onAdd: () => void;
  onIncrement: () => void;
  onDecrement: () => void;
  onDragStart?: (e: ReactDragEvent) => void;
  /** Aviso visible cuando el producto no se puede vender. */
  onBlocked?: (kind: CatalogStockKind) => void;
}

export function ProductCard({
  prod,
  qty,
  selected,
  maxReached,
  monedaCodigo,
  tabIndex,
  buttonRef,
  onKeyDown,
  onAdd,
  onIncrement,
  onDecrement,
  onDragStart,
  onBlocked,
}: ProductCardProps) {
  // Residual stock: catalog snapshot minus what's already in the cart.
  // Cashiers expect "49 disp." → "48 disp." when they add one unit.
  const stock = residualStock(prod.stock_disponible, qty);
  const kind = catalogStockKind(prod);
  const blocked = kind === 'agotado' || kind === 'sin_inventario';
  const blockedLabel = blockedSaleMessage(prod.nombre, kind);
  const sinPrecio = prod.precio <= 0;

  const handleDragStart = (e: ReactDragEvent) => {
    if (blocked) {
      e.preventDefault();
      return;
    }
    const payload: DraggedProduct = { id: prod.id, presentacion_id: prod.presentacion_id };
    e.dataTransfer.setData(DRAG_MIME, JSON.stringify(payload));
    e.dataTransfer.setData('text/plain', prod.nombre);
    e.dataTransfer.effectAllowed = 'copy';
    onDragStart?.(e);
  };

  const mainAction = selected ? onIncrement : onAdd;
  const activate = () => {
    if (blocked) {
      onBlocked?.(kind);
      return;
    }
    mainAction();
  };
  const mainLabel = blockedLabel
    ? blockedLabel
    : selected
      ? `Sumar ${prod.nombre}, ${qty} en carrito${maxReached ? ', máximo disponible alcanzado' : ''}`
      : `Agregar ${prod.nombre} al carrito${stock !== null ? `, ${stock} disponibles` : ''} (Enter)`;

  return (
    <div
      draggable={!blocked}
      onDragStart={handleDragStart}
      className={
        'flex min-h-[124px] flex-col justify-between rounded-md border select-none ' +
        (selected
          ? 'product-card-selected bg-primary-soft'
          : 'bg-neutral-0 hover:border-primary border-neutral-200 hover:shadow-[var(--shadow-ambient-low)]') +
        (blocked ? ' opacity-70' : '')
      }
    >
      <button
        type="button"
        ref={buttonRef}
        tabIndex={tabIndex}
        onClick={activate}
        onKeyDown={onKeyDown}
        aria-label={mainLabel}
        title={mainLabel}
        className={
          'flex flex-1 flex-col items-start rounded-t-md p-3 pb-1 text-left transition-colors duration-150 ' +
          (blocked ? 'cursor-not-allowed' : 'cursor-pointer active:bg-neutral-100')
        }
      >
        <span className="flex w-full items-start justify-between gap-2">
          <div className="flex min-w-0 items-start gap-2">
            <ProductImage src={prod.imagen_url} alt={prod.nombre} className="mt-0.5 h-8 w-8" />
            <span className="min-w-0">
              <span className="block text-sm leading-snug font-semibold text-neutral-900 capitalize">
                {prod.nombre}
              </span>
              <span className="mt-0.5 block text-xs text-neutral-500">
                {prod.presentacion_nombre}
              </span>
            </span>
          </div>
          <span
            className={`tnum rounded-pill bg-primary text-neutral-0 inline-flex shrink-0 items-center justify-center px-2 py-0.5 text-[11px] font-bold shadow-[var(--shadow-ambient-low)] ${
              selected ? 'badge-visible' : 'badge-placeholder'
            }`}
            aria-hidden="true"
          >
            ×{qty}
          </span>
        </span>
        <span className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="tnum text-sm font-semibold text-neutral-900">
            {formatMoney(prod.precio, monedaCodigo)}
          </span>
          {sinPrecio && (
            <span className="rounded-pill bg-warning-soft text-warning inline-flex items-center px-2 py-0.5 text-[11px] font-medium">
              Sin precio
            </span>
          )}
          {kind === 'sin_inventario' && (
            <span className="rounded-pill bg-warning-soft text-warning inline-flex items-center px-2 py-0.5 text-[11px] font-medium">
              Sin inventario en esta bodega
            </span>
          )}
          {stock !== null &&
            kind !== 'sin_inventario' &&
            (kind === 'agotado' ? (
              <span className="rounded-pill bg-danger-soft text-danger inline-flex items-center px-2 py-0.5 text-[11px] font-medium">
                Agotado
              </span>
            ) : stock <= LOW_STOCK_THRESHOLD ? (
              <span className="tnum rounded-pill bg-warning-soft text-warning inline-flex items-center px-2 py-0.5 text-[11px] font-medium">
                ¡{stock === 1 ? 'Queda 1' : `Quedan ${stock}`}!
              </span>
            ) : (
              <span className="tnum rounded-pill inline-flex items-center bg-neutral-100 px-2 py-0.5 text-[11px] font-medium text-neutral-700">
                {stock} disp.
              </span>
            ))}
        </span>
      </button>

      <div className="flex items-center justify-end px-3 pb-3">
        {selected ? (
          <div className="bg-neutral-0 flex items-center overflow-hidden rounded-md border border-neutral-300">
            <button
              type="button"
              tabIndex={-1}
              onClick={onDecrement}
              title={`Restar ${prod.nombre} (−)`}
              className="flex h-11 w-11 items-center justify-center text-neutral-700 transition-colors hover:bg-neutral-100"
              aria-label={`Restar ${prod.nombre} (−)`}
            >
              {qty === 1 ? (
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <Minus className="h-3.5 w-3.5" aria-hidden="true" />
              )}
            </button>
            <span
              className="tnum min-w-7 px-1.5 text-center text-xs font-bold text-neutral-900"
              aria-live="polite"
              aria-atomic="true"
            >
              {qty}
            </span>
            <button
              type="button"
              tabIndex={-1}
              onClick={onIncrement}
              disabled={maxReached}
              title={maxReached ? `Máximo disponible: ${stock}` : `Sumar ${prod.nombre} (+)`}
              className="flex h-11 w-11 items-center justify-center text-neutral-700 transition-colors hover:bg-neutral-100 disabled:cursor-not-allowed disabled:text-neutral-300 disabled:hover:bg-transparent"
              aria-label={
                maxReached
                  ? `Máximo disponible: ${stock} unidades de ${prod.nombre}`
                  : `Sumar ${prod.nombre} (+)`
              }
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            tabIndex={-1}
            onClick={activate}
            title={blockedLabel ?? `Agregar ${prod.nombre} (Enter)`}
            className={
              'bg-neutral-0 flex h-11 w-11 items-center justify-center rounded-md border text-neutral-700 transition-colors ' +
              (blocked
                ? 'cursor-not-allowed border-neutral-200 text-neutral-300'
                : 'hover:border-primary hover:bg-primary-soft hover:text-primary border-neutral-300')
            }
            aria-label={blockedLabel ?? `Agregar ${prod.nombre}`}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}
